import { useState } from 'react';
import { useMilestoneTemplates } from '@/hooks/useMilestoneTemplates';
import { useCatalog } from '@/hooks/useCatalog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, GripVertical, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { MilestoneTemplateForm } from '@/components/settings/MilestoneTemplateForm';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

export default function MilestoneTemplatesSettings() {
  const [selectedSalesItem, setSelectedSalesItem] = useState<string>('');
  const { templates, isLoading, deleteTemplate } = useMilestoneTemplates(selectedSalesItem);
  const { items: catalog } = useCatalog();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any>(null);

  const caseTypes = catalog?.filter(item => item.type === 'case') || [];

  const handleDelete = async (id: string) => {
    await deleteTemplate.mutateAsync(id);
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Templates de Marcos</h1>
          <p className="text-muted-foreground">
            Configure os marcos para cada tipo de processo
          </p>
        </div>
        <Dialog open={isCreateOpen || !!editingTemplate} onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) setEditingTemplate(null);
        }}>
          <DialogTrigger asChild>
            <Button disabled={!selectedSalesItem}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Marco
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTemplate ? 'Editar' : 'Criar'} Template de Marco</DialogTitle>
            </DialogHeader>
            <MilestoneTemplateForm 
              salesItemId={selectedSalesItem}
              template={editingTemplate}
              onSuccess={() => {
                setIsCreateOpen(false);
                setEditingTemplate(null);
              }} 
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Selecione o Tipo de Processo</CardTitle>
          <CardDescription>
            Escolha um tipo de processo para configurar seus marcos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedSalesItem} onValueChange={setSelectedSalesItem}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione um tipo de processo" />
            </SelectTrigger>
            <SelectContent>
              {caseTypes.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedSalesItem && (
        <div className="space-y-4">
          {isLoading ? (
            <div>Carregando...</div>
          ) : templates && templates.length > 0 ? (
            templates.map((template: any) => (
              <Card key={template.id}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-5 h-5 text-muted-foreground" />
                    <div className="flex-1">
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      {template.description && (
                        <CardDescription>{template.description}</CardDescription>
                      )}
                    </div>
                    <div className="flex gap-2 items-center">
                      {template.estimated_duration_days && (
                        <Badge variant="outline">
                          {template.estimated_duration_days} dias
                        </Badge>
                      )}
                      {template.auto_complete_on_event_code && (
                        <Badge variant="secondary">
                          Auto: evento {template.auto_complete_on_event_code}
                        </Badge>
                      )}
                      {template.auto_complete_on_task_code && (
                        <Badge variant="secondary">
                          Auto: tarefa {template.auto_complete_on_task_code}
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingTemplate(template)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir este template de marco? Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(template.id)}>
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Nenhum marco configurado para este tipo de processo.
                <br />
                Clique em "Novo Marco" para começar.
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
